/**
 * The Reporting Dashboard's figures, from the warehouse.
 *
 * WHAT THIS REPLACES
 *
 * The page called runQuery/runPreviousPeriod/runWindow from metrics.js, which
 * generated its own data in the browser — and then ran one more query PER
 * CAMPAIGN ROW to draw the sparklines. Free when the rows were invented;
 * a dozen round trips to BigQuery per page load once they are not.
 *
 * So the campaign trends come back in a single campaign-by-date query and are
 * bucketed here by campaign. Bucketing is not arithmetic: no figure is summed,
 * divided or averaged in this file. Every number on the page is computed by
 * BigQuery from the client's own metric registry, which is what lets the
 * dashboard and the AI Insights answer agree.
 *
 * FOUR REQUESTS, FOUR JOBS, NOT SEVEN
 *
 *   totals          no grouping, with the previous period            → tiles
 *   byDate          grouped by date, with the previous period        → chart
 *   byCampaign      grouped by campaign, sorted                      → table
 *   campaignByDate  campaign × date                                  → sparklines
 *
 * byDate is not derived from campaignByDate even though it could be, because
 * deriving it would mean summing a column of ratios in the browser — and the
 * period CTR is not the mean of the daily CTRs. BigQuery recomputes each ratio
 * from the summed numerator and denominator at whatever grain is asked for;
 * that is the whole reason the registry exists.
 *
 * ONE ADVERTISER AT A TIME
 *
 * A client here is one advertiser, and the roll-up this page performs is
 * across that advertiser's Google and Meta accounts — which gold already did
 * before these queries ran. It is not a roll-up across advertisers: each has
 * its own dataset, and nothing here can read two of them.
 */

import { reportingPost } from '../api';

/** Days x campaigns. Ninety days of thirty campaigns is 2,700, so this cuts. */
const CAMPAIGN_BY_DATE_LIMIT = 1000;

/** Metrics the dashboard asks for, in the order the tiles want them. */
export const TILE_METRICS = ['conversions', 'spend', 'cpa', 'roas'];
export const TABLE_METRICS = [
  'spend', 'impressions', 'link_clicks', 'conversions', 'cpa', 'ctr', 'roas',
];

// Every metric either view needs, de-duplicated. One list keeps the tiles and
// the table on the same query, so a figure cannot differ between them.
const ALL_METRICS = [...new Set([...TILE_METRICS, ...TABLE_METRICS])];

const splitPeriods = (rows) => ({
  current: rows.filter((r) => r.period !== 'previous'),
  previous: rows.filter((r) => r.period === 'previous'),
});

/**
 * @param {string} clientId  the advertiser to report on. Required: there is no
 *                           all-advertisers view, by design.
 * @param {number} days      window length, counting back from yesterday
 * @param {string} sortKey   metric to rank campaigns by
 */
// ACCOUNTS WITHIN AN ADVERTISER, NOT ADVERTISERS.
//
// The default is every account the advertiser has — the combined Google and
// Meta view, which is what gold.ad_daily already is. The filter narrows to one
// platform when someone wants to look at their Meta Ads Manager on its own.
// Today an advertiser has one account per platform, so platform and account are
// the same choice; when that stops being true this becomes a filter on
// account_id, which is already a dimension.
const base = ({ clientId, days, platform }) => ({
  client_id: clientId,
  grain: 'campaign',
  range_days: days,
  filters: platform
    ? [{ dimension: 'platform', op: 'is', values: [platform] }]
    : [],
});

// A GROUPED REQUEST DECLINES THE PERIOD TOTAL, IN ONE PLACE.
//
// At module scope rather than inside loadDashboard because the sparkline query
// moved out of it and needs the same flag. A grouped request that forgets it is
// correct and slow and invisible, so there is one object that sets it and every
// grouped request spreads that.
const grouped = (selection) => ({ ...base(selection), include_totals: false });

export async function loadDashboard({ clientId, days, sortKey, platform }) {
  const selection = { clientId, days, platform };

  // THE PERIOD TOTAL IS ASKED FOR ONCE, NOT FOUR TIMES.
  //
  // A grouped query costs a second BigQuery job on the server, because a
  // period total cannot be derived from a page of grouped rows without
  // averaging ratios — and the period CTR is not the mean of the daily CTRs.
  // That is correct and it is why the first query exists.
  //
  // The other three were paying for it too, and this file reads totals and
  // change from the undimensioned request and from nowhere else. Three of the
  // seven jobs a page load ran were computing a figure nothing looked at.
  const descending = !['cpa', 'cpc', 'cpm'].includes(sortKey);

  // THREE QUERIES BLOCK THE PAGE, NOT FOUR.
  //
  // The fourth is campaign x date - a thousand rows whose only job is the
  // sparkline in each table row. It is the largest of the four and nothing
  // above the table needs it, so waiting for it meant the tiles, the chart
  // and the table all appeared at the speed of the slowest thing on the page.
  //
  // It is fetched separately by loadCampaignTrends, and the page holds the
  // result in its own state rather than merging it into this payload - the
  // two requests race by design, and a merge loses whichever lands first.
  // The table renders without sparklines for a moment, which it already knew
  // how to do: a cut series makes a confidently wrong trend, so the row draws
  // nothing rather than guess.
  const [totals, byDate, byCampaign] = await Promise.all([
    reportingPost('/reporting/query', {
      ...base(selection), metrics: ALL_METRICS, compare_previous: true,
    }),
    reportingPost('/reporting/query', {
      ...grouped(selection), metrics: ALL_METRICS, dimensions: ['date'],
      compare_previous: true,
      limit: 400,
    }),
    reportingPost('/reporting/query', {
      ...grouped(selection), metrics: ALL_METRICS, dimensions: ['campaign_name'],
      sort_by: sortKey, sort_dir: descending ? 'desc' : 'asc', limit: 100,
    }),
  ]);

  const dates = splitPeriods(byDate.rows || []);

  const totalRows = totals.rows || [];
  return {
    range: totals.window,
    labels: Object.fromEntries(
      (totals.columns || []).map((c) => [c.name, c.label]),
    ),
    totals: totalRows.find((r) => r.period !== 'previous') || totalRows[0] || null,
    previousTotals: totalRows.find((r) => r.period === 'previous') || null,
    // Supplied by the server so nothing here subtracts one figure from
    // another — the same reason the narrated answers are handed their deltas.
    change: totals.change || null,
    byDate: dates.current,
    prevByDate: dates.previous,
    byCampaign: byCampaign.rows || [],
    // No trends here. They were briefly returned empty so the page could
    // merge the real ones in later, and that was the bug: a load finishing
    // after the series had already landed overwrote them with the empty map.
    // The series are their own state now, stamped with the selection they are
    // for, and this payload does not mention them.
    caveats: totals.caveats || [],
  };
}


/**
 * The sparkline series, fetched after the page has already drawn.
 *
 * Split out because it is the heaviest of the four queries and the least
 * urgent: a thousand rows of campaign x date, feeding a 60-pixel line in each
 * table row. Blocking the tiles and the chart on it made the whole page as
 * slow as its smallest ornament.
 */
export async function loadCampaignTrends({ clientId, days, platform }) {
  // Named rather than spread inline: the page's own test finds a request by
  // reading as far as the first closing brace, and a nested call puts one
  // there. Cheap to keep the literal flat.
  const selection = { clientId, days, platform };

  const result = await reportingPost('/reporting/query', {
    ...grouped(selection),
    metrics: ALL_METRICS,
    dimensions: ['campaign_name', 'date'],
    limit: CAMPAIGN_BY_DATE_LIMIT,
  });

  const rows = result.rows || [];

  // The server refuses a larger limit rather than truncating, but a result
  // landing exactly on it may have been cut - and a sparkline drawn from a
  // cut series is a wrong trend, drawn confidently. Flagged so the page draws
  // nothing instead.
  const trendsComplete = rows.length < CAMPAIGN_BY_DATE_LIMIT;

  // campaign -> rows, in date order. The server sorts by date when it is a
  // dimension, so the order arrives correct and is not re-sorted here.
  const trends = new Map();
  for (const row of rows) {
    const key = row.campaign_name;
    if (!trends.has(key)) trends.set(key, []);
    trends.get(key).push(row);
  }

  return { trends, trendsComplete };
}
